import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui';
import { formatCurrency } from '../../../../utils/formatters/statusFormatters';
import { projectAPI } from '../../../../services/realApi';
import AddPersonModal from './AddPersonModal';
import { TransformedProject } from '../hooks/useProjects';
import { Person } from '../hooks/usePeople';

interface Props {
  project: TransformedProject | null;
}

export const DeliveryPod: React.FC<Props> = ({ project }) => {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);

  useEffect(() => {
    if (project?.id) {
      loadPeople();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const loadPeople = async () => {
    if (!project) return;
    setLoading(true);
    try {
      const result = await projectAPI.getProjectPeople(project.id);
      if (result.success) {
        setPeople(result.data ?? []);
      } else {
        console.error('Error loading people:', result.error);
      }
    } catch (err: unknown) {
      console.error('Error loading people:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPerson = () => {
    setEditingPerson(null);
    setShowAddModal(true);
  };

  const handleEditPerson = (person: Person) => {
    setEditingPerson(person);
    setShowAddModal(true);
  };

  const handleRemovePerson = async (personId: string) => {
    if (!project) return;
    if (!globalThis.confirm('Are you sure you want to remove this person from the project?')) {
      return;
    }

    try {
      const result = await projectAPI.removeProjectPerson(project.id, personId);
      if (result.success) {
        await loadPeople();
      } else {
        alert('Failed to remove person: ' + result.error);
      }
    } catch (err: unknown) {
      console.error('Error removing person:', err);
      alert('Failed to remove person');
    }
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditingPerson(null);
  };

  const handleModalSuccess = async () => {
    setShowAddModal(false);
    setEditingPerson(null);
    setTimeout(() => {
      loadPeople();
    }, 100);
  };

  if (!project) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Delivery Pod</CardTitle>
          <button
            onClick={handleAddPerson}
            className="w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center"
          >
            +
          </button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : people.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No team members assigned to this project yet
            </div>
          ) : (
            <div className="space-y-3">
              {people.map((person) => (
                <div
                  key={person.id}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
                        {person.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {person.name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {person.role}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                          <span className="text-gray-500 dark:text-gray-400">
                            Email: {person.email}
                          </span>
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            Hourly Rate: {formatCurrency(person.hourlyRate ?? 0)}
                          </span>
                        </div>
                        {person.partnerName && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            Partner: {person.partnerName}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleEditPerson(person)}
                        className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleRemovePerson(person.id)}
                        className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Remove"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showAddModal && (
        <AddPersonModal
          project={project}
          person={editingPerson}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
};

export default DeliveryPod;
